import axios from 'axios';
import {
  QueryStatus, useMutation, useQuery, useQueryClient,
} from 'react-query';
import { useToast } from '@chakra-ui/react';
import { NIL } from 'uuid';
import { SqlDateTimeToDateField } from '../utils/SqlTools';
import {
  useVoidDialog, VoidAlternative, VoidChoice, VoidDocType,
} from '../shared/VoidDialogProvider';

// Generic data layer shared by the five document types (Invoice, Bill, Proposal,
// PurchaseOrder, WorkOrder). Endpoint paths and react-query keys differ per type —
// that's the DocumentConfig; everything else (detail query, save, archive, void,
// status change, cache invalidation) is identical and lives here exactly once.

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

export interface DocumentLike<TStatus> {
  id: string;
  date?: string;
  projectId?: string;
  status?: TStatus;
}

export interface DocumentConfig {
  listKey: string; // react-query key the list page caches under, e.g. 'invoices'
  detailKey: string; // react-query key prefix for the detail query, e.g. 'invoice'
  basePath: string; // REST base path, e.g. 'invoice' or 'bills'
  archivePath?: (id: string) => string;
  voidPath?: (id: string) => string;
  // EPIC_VOID_FLOW_2026-10 — the server's document type for the void dialog's preview
  voidType?: VoidDocType;
  unvoidPath?: (id: string) => string;
  finalizePath?: (id: string) => string; // item 38: invoices lock permanently
  statusPath?: (id: string) => string;
  statusAsJson?: boolean; // invoice/bills post the enum with an explicit JSON content-type
  // item 485/534 — archive semantics differ by doc type. Void ALWAYS stays badged in the ledger.
  // For docs whose main list EXCLUDES archived (e.g. proposals — `Search` filters !IsArchive), archive
  // MOVES the row OUT, so we optimistically REMOVE it (instant feedback) instead of marking-and-keeping
  // a row that looks unchanged and lingers until refetch. `archivedKey` is the archived-view list to
  // refresh so the row shows up there.
  archiveRemovesRow?: boolean;
  archivedKey?: string;
}

export interface DocumentApi<TDoc, TStatus> {
  data: TDoc | undefined;
  status: QueryStatus;
  // imperative detail fetch — grids need the FULL document (list rows carry no
  // items array) before duplicating or copying to another project
  fetchById: (id: string) => Promise<TDoc | undefined>;
  tryUpdate: (data: TDoc | undefined) => Promise<unknown>;
  tryStatus: (id: string, newStatus: TStatus) => void;
  tryArchive: (id: string) => void;
  tryVoid: (id: string, alternative?: VoidAlternative) => Promise<boolean>; // item 1582
  voidMany: (ids: string[], alternative?: VoidAlternative) => Promise<boolean>; // EPIC_VOID_FLOW — one dialog for a bulk void
  tryUnvoid: (id: string) => void;
  // item 1221 — extraQuery rides the finalize POST (e.g. applyCreditMemoId=…), applied
  // atomically with the lock server-side
  tryFinalize: (id: string, extraQuery?: string) => Promise<unknown>;
  // item (Chad 2026-07-16) — the id currently mutating, so a grid can show a per-row spinner
  unvoidingId: string | null;
  voidingId: string | null;
  archivingId: string | null;
}

const useDocument = <TStatus, TDoc extends DocumentLike<TStatus>>(
  cfg: DocumentConfig,
  props: { id?: string },
): DocumentApi<TDoc, TStatus> => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const getById = async (id: string | undefined): Promise<TDoc | undefined> => {
    if (id === undefined) return undefined;
    const { data } = await axios.get(`${apiUrl()}${cfg.basePath}/${id}`);
    data.date = SqlDateTimeToDateField(data.date);
    return data;
  };

  // item 795 (Chad) — return the doc id so a brand-new save can land ON the created doc
  // (the create endpoint already returns the new Guid; we were discarding it, which forced
  // new docs to bounce to the project — with nowhere to then upload the client PO).
  const postDocument = async (doc: TDoc): Promise<string> => {
    if (doc.id !== NIL) {
      await axios.post(`${apiUrl()}${cfg.basePath}/${doc.id}`, doc);
      return doc.id;
    }
    const { data } = await axios.post(`${apiUrl()}${cfg.basePath}`, doc);
    const newId = typeof data === 'string' ? data : (data?.id ?? NIL);
    // item 1276 (Chad: "forms are not saving on first save — POC, description… all six types") —
    // CREATE-DROP IMMUNITY. The per-type CREATE actions have repeatedly dropped fields the form
    // offered (alternate contact → QB ids → portal toggles → now doc text fields): the create
    // ctor takes a fixed arg list, and anything outside it is silently discarded until the next
    // edit. Root-fixing six ctors one field at a time keeps losing. Instead: the moment a create
    // succeeds, RE-POST the same values as an UPDATE onto the new id — the update actions
    // persist every field the form can hold, by design. Idempotent (same data), one extra call
    // per create, and the whole bug class dies for every document type at once. Best-effort:
    // if the follow-up update fails, the created doc still stands and the form lands on it.
    if (newId && newId !== NIL) {
      try {
        await axios.post(`${apiUrl()}${cfg.basePath}/${newId}`, { ...doc, id: newId });
      } catch { /* create stands; the landing form shows what persisted */ }
    }
    return newId;
  };

  const { data, status } = useQuery([cfg.detailKey, props.id], () => getById(props.id), {
    refetchOnWindowFocus: false,
    enabled: props.id !== NIL,
  });

  const fetchById = (id: string) => queryClient.fetchQuery(
    [cfg.detailKey, id],
    () => getById(id),
  );

  const doUpdate = useMutation((newData: TDoc) => postDocument(newData), {
    onMutate: async (doc) => {
      await queryClient.cancelQueries(cfg.listKey);
      await queryClient.cancelQueries([cfg.detailKey, doc.id]);
      await queryClient.cancelQueries(['project']);
    },
    onSettled: async (result, err, doc) => {
      await queryClient.invalidateQueries(cfg.listKey);
      // invalidate ALL project queries so a re-parented proposal refreshes both the project it
      // moved OUT of and the one it moved INTO. The key MUST be the array prefix ['project'] —
      // a bare string 'project' does NOT match array keys ['project', id] in react-query v3.
      // Do NOT refetchInactive: some components hold a disabled useProject({ id: NIL }) query, and
      // force-refetching those hit GET /project/000…000 → 404, which rejected the whole
      // invalidation. The active (viewed) project refetches here; others refetch on navigation via
      // useProject's refetchOnMount:'always'. (dart 2026-07-14)
      await queryClient.invalidateQueries(['project']);
      await queryClient.invalidateQueries([cfg.detailKey, doc.id]);
    },
  });
  const tryUpdate = (result: TDoc | undefined): Promise<string | void> => {
    if (result === undefined) return Promise.resolve();
    // resolves to the doc id (new or existing) — item 795
    return doUpdate.mutateAsync(result);
  };

  const postStatus = async (id: string, newStatus: TStatus) => {
    if (cfg.statusPath === undefined) return;
    await axios.post(
      `${apiUrl()}${cfg.statusPath(id)}`,
      newStatus,
      cfg.statusAsJson ? { headers: { 'content-type': 'application/json' } } : undefined,
    );
  };
  const doStatus = useMutation(
    (variables: { id: string, newStatus: TStatus }) => postStatus(variables.id, variables.newStatus),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(cfg.listKey);
      },
      onSuccess: async (result, variables) => {
        // flip the status on the cached list row so the grid updates instantly
        const list: { index: TDoc[] } | undefined = queryClient.getQueryData(cfg.listKey);
        const row = list?.index.find((d) => d.id === variables.id);
        if (row === undefined) return;
        row.status = variables.newStatus;
        queryClient.setQueryData(cfg.listKey, list);
      },
      // surface a rejected status change instead of silently doing nothing — e.g.
      // a locked/closed project or a disallowed transition (item 110)
      onError: (err: any) => {
        const msg = err?.response?.data?.errors?.[0]
          ?? err?.response?.data
          ?? 'The status could not be changed — the document or its project may be locked.';
        toast({
          status: 'error',
          title: 'Status not changed',
          description: typeof msg === 'string' ? msg : undefined,
          duration: 7000,
          isClosable: true,
        });
      },
      onSettled: async (result, err, variables) => {
        await queryClient.invalidateQueries(cfg.listKey);
        await queryClient.invalidateQueries([cfg.detailKey, variables.id]);
        // the project detail query embeds this doc — refresh it so a status change
        // shows in the project view, not just the standalone grid (item 110)
        await queryClient.invalidateQueries(['project']); // item 1074 — array prefix, see doUpdate's comment
      },
    },
  );
  const tryStatus = (id: string, newStatus: TStatus) => {
    doStatus.mutate({ id, newStatus });
  };

  const postAction = async (path: ((id: string) => string) | undefined, id: string) => {
    if (path === undefined) return;
    await axios.post(`${apiUrl()}${path(id)}`, id);
  };
  // item 534 — void/archive should MARK the row in place and keep it in the ledger, not make
  // it vanish-then-reappear on refetch (Chad). Optimistically flag the cached list row so the
  // grid updates instantly + smoothly; the background refetch then confirms (the list already
  // includes void/archived rows). `field` is 'isVoid' or 'isArchive'.
  const markRow = (id: string, field: 'isVoid' | 'isArchive', value = true) => {
    const list: { index: TDoc[] } | undefined = queryClient.getQueryData(cfg.listKey);
    const row = list?.index.find((d) => d.id === id) as Record<string, unknown> | undefined;
    if (row === undefined) return;
    row[field] = value;
    queryClient.setQueryData(cfg.listKey, list);
  };
  // item 485 — drop a row from the cached list (archive moving it OUT of the main list)
  const removeRow = (id: string) => {
    const list: { index: TDoc[] } | undefined = queryClient.getQueryData(cfg.listKey);
    if (list === undefined) return;
    queryClient.setQueryData(cfg.listKey, { ...list, index: list.index.filter((d) => d.id !== id) });
  };
  // item 1074 — archive/void failures used to be SILENT (no onError): the click looked
  // ignored ("I am not able to archive this invoice why?"). Surface the server's reason.
  const actionErrorToast = (title: string) => (err: any) => {
    const msg = err?.response?.data?.errors?.[0]
      ?? err?.response?.data
      ?? 'The server rejected the change — the document or its project may be locked.';
    toast({
      status: 'error',
      title,
      description: typeof msg === 'string' ? msg : undefined,
      duration: 8000,
      isClosable: true,
    });
  };
  const doArchive = useMutation((id: string) => postAction(cfg.archivePath, id), {
    onMutate: async (id) => {
      await queryClient.cancelQueries(cfg.listKey);
      // proposals: archive moves the row OUT of the main list → remove it (instant feedback).
      // other docs: archive stays badged in place (item 534) → mark it.
      if (cfg.archiveRemovesRow) removeRow(id);
      else markRow(id, 'isArchive');
    },
    onError: (err: any, id: string) => {
      markRow(id, 'isArchive', false); // roll back the optimistic mark (item 1074)
      actionErrorToast('Not archived')(err);
    },
    onSettled: async (result, err, id) => {
      await queryClient.invalidateQueries(cfg.listKey);
      await queryClient.invalidateQueries([cfg.detailKey, id]);
      await queryClient.invalidateQueries(['project']); // item 1074 — array prefix, see doUpdate's comment
      if (cfg.archivedKey) await queryClient.invalidateQueries(cfg.archivedKey);
    },
  });
  const tryArchive = (id: string) => {
    doArchive.mutate(id);
  };

  // EPIC_VOID_FLOW_2026-10 (item 1571) — every void goes through the shared dialog: a required
  // reason + the "Notify customer" choice ride the POST body (the server emails the team, and the
  // customer only when ticked and they actually received it). Grids never pre-confirm a void.
  const openVoidDialog = useVoidDialog();
  const doVoid = useMutation(
    async ({ id, choice }: { id: string; choice: VoidChoice }) => {
      if (cfg.voidPath === undefined) return undefined;
      const r = await axios.post(`${apiUrl()}${cfg.voidPath(id)}`, choice);
      return r.data as { customerNotified?: boolean; customerRecipients?: string } | undefined;
    },
    {
      onMutate: async ({ id }) => {
        await queryClient.cancelQueries(cfg.listKey);
        markRow(id, 'isVoid');
      },
      onError: (err: any, { id }) => {
        markRow(id, 'isVoid', false); // roll back the optimistic mark (item 1074)
        actionErrorToast('Not voided')(err);
      },
      onSettled: async (result, err, { id }) => {
        await queryClient.invalidateQueries(cfg.listKey);
        await queryClient.invalidateQueries([cfg.detailKey, id]);
        // voiding an invoice can reopen its work order (item 38)
        await queryClient.invalidateQueries('workOrders');
        await queryClient.invalidateQueries(['project']); // item 1074 — array prefix, see doUpdate's comment
        await queryClient.invalidateQueries('accountant-adjustments'); // item 1572 — the VOIDED row
      },
    },
  );
  // one dialog for one or many documents; resolves false when cancelled
  const voidMany = async (ids: string[], alternative?: VoidAlternative): Promise<boolean> => {
    if (cfg.voidPath === undefined || cfg.voidType === undefined || ids.length === 0) return false;
    const choice = await openVoidDialog({ type: cfg.voidType, ids, alternative });
    if (choice === null) return false;
    const results = await Promise.all(ids.map((id) => doVoid.mutateAsync({ id, choice }).catch(() => null)));
    const done = results.filter((r) => r !== null);
    if (done.length > 0) {
      const told = done.filter((r) => r?.customerNotified).length;
      toast({
        status: 'success',
        title: ids.length === 1 ? 'Voided' : `Voided ${done.length} of ${ids.length}`,
        description: told > 0
          ? `Your team was emailed the reason; ${ids.length === 1 ? `the ${cfg.voidType === 'PurchaseOrder' ? 'vendor' : 'customer'} was notified (${done[0]?.customerRecipients ?? ''})` : `${told} customer notice${told === 1 ? '' : 's'} sent`}.`
          : 'Your team was emailed the reason.',
        duration: 7000,
        isClosable: true,
      });
    }
    return done.length > 0;
  };
  const tryVoid = (id: string, alternative?: VoidAlternative) => voidMany([id], alternative);

  // item 549 — un-void: a voided doc STAYS in the list (marked VOID) and can be restored
  // (Chad). Only wired when the type sets an unvoidPath (e.g. proposal/UnVoidProposal/{id}).
  const doUnvoid = useMutation((id: string) => postAction(cfg.unvoidPath, id), {
    onMutate: async (id) => {
      await queryClient.cancelQueries(cfg.listKey);
      markRow(id, 'isVoid', false);
    },
    onSettled: async (result, err, id) => {
      await queryClient.invalidateQueries(cfg.listKey);
      await queryClient.invalidateQueries([cfg.detailKey, id]);
      await queryClient.invalidateQueries('workOrders');
      await queryClient.invalidateQueries(['project']); // item 1074 — array prefix, see doUpdate's comment
    },
  });
  const tryUnvoid = (id: string) => {
    doUnvoid.mutate(id);
  };

  // item 38: finalize locks an invoice permanently — also reloads the source work
  // order (its status may reopen on later void), so invalidate work orders too.
  const doFinalize = useMutation(
    ({ id, extraQuery }: { id: string; extraQuery?: string }) => {
      if (cfg.finalizePath === undefined) return Promise.resolve();
      // item 1221 — extraQuery (e.g. applyCreditMemoId=…) rides the same POST, atomic server-side
      const path = cfg.finalizePath(id) + (extraQuery ? `?${extraQuery}` : '');
      return axios.post(`${apiUrl()}${path}`, id).then(() => undefined);
    },
    {
      onSettled: async (result, err, vars) => {
        await queryClient.invalidateQueries(cfg.listKey);
        await queryClient.invalidateQueries([cfg.detailKey, vars.id]);
        await queryClient.invalidateQueries('workOrders');
        await queryClient.invalidateQueries(['project']); // item 1074 — array prefix, see doUpdate's comment
        await queryClient.invalidateQueries('credit-memos'); // item 1221 — an applied memo shrinks
      },
    },
  );
  const tryFinalize = (id: string, extraQuery?: string) => doFinalize.mutateAsync({ id, extraQuery });

  return {
    data,
    status,
    fetchById,
    tryUpdate,
    tryStatus,
    tryArchive,
    tryVoid,
    voidMany,
    tryUnvoid,
    tryFinalize,
    unvoidingId: doUnvoid.isLoading ? (doUnvoid.variables ?? null) : null,
    voidingId: doVoid.isLoading ? (doVoid.variables?.id ?? null) : null,
    archivingId: doArchive.isLoading ? (doArchive.variables ?? null) : null,
  };
};

// For per-type extra transitions (e.g. proposal approve/decline): posts to the given
// path and optimistically applies `appliedStatus` to the cached list row.
export const useDocumentStatusAction = <TStatus, TDoc extends DocumentLike<TStatus>>(
  cfg: DocumentConfig,
  pathFn: (id: string) => string,
  appliedStatus: TStatus,
): ((id: string) => void) => {
  const queryClient = useQueryClient();
  const mutation = useMutation((id: string) => axios.post(`${apiUrl()}${pathFn(id)}`, id).then(() => undefined), {
    onMutate: async () => {
      await queryClient.cancelQueries(cfg.listKey);
    },
    onSuccess: async (result, id) => {
      const list: { index: TDoc[] } | undefined = queryClient.getQueryData(cfg.listKey);
      const row = list?.index.find((d) => d.id === id);
      if (row === undefined) return;
      row.status = appliedStatus;
      queryClient.setQueryData(cfg.listKey, list);
    },
    onSettled: async (result, err, id) => {
      await queryClient.invalidateQueries(cfg.listKey);
      await queryClient.invalidateQueries([cfg.detailKey, id]);
    },
  });
  return (id: string) => {
    mutation.mutate(id);
  };
};

export default useDocument;
